import { z } from 'zod';

/**
 * A scanned signature, carried inline as a data URL.
 *
 * ## Why a data URL rather than a file
 *
 * `server/print/engine/fonts.ts` sets pdfmake's `setUrlAccessPolicy` and
 * `setLocalAccessPolicy` deny-by-default, because a print template is
 * user-authored data reaching a renderer that would otherwise happily fetch
 * `{ image: 'http://…' }` or read `{ image: '/etc/passwd' }`. So:
 *
 *   * a hosted URL is out — allowing it turns the renderer into an SSRF forwarder;
 *   * a file on disk is out — it would need the local allow-list to become a
 *     dynamic per-row set rebuilt on every render, which is deny-by-default in
 *     name only.
 *
 * A data URL consults neither policy. That is not a workaround; it is the same
 * reason `printLogoSchema.data_url` already works this way.
 *
 * ## The ceiling
 *
 * A signature scanned at 60 × 20 mm and 300 dpi is roughly 700 × 240 px, which
 * is 30–60 KB as a transparent PNG. 256 KB is generous for that and small
 * enough that a report carrying three of them stays well inside the request
 * body limit. The same number is restated as a `CHECK` in the migration,
 * because a direct psql write bypasses Zod and the value ends up in a renderer.
 *
 * GIF is not accepted, unlike the letterhead logo: an animated signature is not
 * a thing, and the logo only keeps it for continuity with what labs already had.
 */

/** Bytes of base64 string, not bytes of image — base64 inflates by about 4/3. */
export const SIGNATURE_MAX_BYTES = 256 * 1024;

export const SIGNATURE_ACCEPTED_TYPES = ['image/png', 'image/jpeg'] as const;

export const signatureDataUrlSchema = z
  // `''` is a member of the union rather than something the transform mops up
  // afterwards: a `.transform` runs *after* validation, so an empty string
  // would be rejected by the regex before it ever reached one. It means "no
  // signature", not "a malformed one" — this is the same shape `optionalEmail`
  // uses, for the same reason.
  .union([
    z.literal(''),
    z
      .string()
      .max(SIGNATURE_MAX_BYTES, 'Signature must be under 256 KB')
      .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, 'Upload a PNG or JPEG'),
  ])
  .nullish()
  .transform((value) => value || null);

/** The lab mark. Wider than a signature and accepts GIF, matching printLogoSchema. */
export const LOGO_MAX_BYTES = 512 * 1024;

export const LOGO_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif'] as const;

export const logoDataUrlSchema = z
  .union([
    z.literal(''),
    z
      .string()
      .max(LOGO_MAX_BYTES, 'Logo must be under 512 KB')
      .regex(/^data:image\/(png|jpeg|gif);base64,[A-Za-z0-9+/=]+$/, 'Upload a PNG, JPEG or GIF'),
  ])
  .nullish()
  .transform((value) => value || null);
